/* eslint-disable jsx-a11y/aria-proptypes */
/* eslint-disable prettier/prettier */
'use client';
import { Content } from '.generated/Feature.CW.model';
import { Text, useSitecore } from '@sitecore-content-sdk/nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { isDefaultDate } from 'lib/constants';
import { getCookie, setCookie } from 'lib/utils/utilityFunction';
import { JSX, useEffect, useState } from 'react';
import { tv } from 'tailwind-variants';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';

export type AlertProps = Content.Alert & {
  displayName?: string;
  id?: string;
};

const Alert = (props: { type?: 'warning' | 'neutral' }): JSX.Element => {
  const {
    wrapper,
    content,
    titleWrapper,
    alertIcon,
    title,
    closeIcon,
    description,
    linkWrapper,
    closeButton,
  } = TailwindVariants();

  const { page } = useSitecore();

  const fieldData = page?.layout?.sitecore?.route?.fields?.Alert as AlertProps; // Page level alert data
  const siteSettings = useSiteSettings()?.fields;
  const globalPageAlert = siteSettings?.globalPageAlert as AlertProps; // Global Alert data

  // Check if global alert exists and has not expired
  const globalAlertExpirationDate = globalPageAlert?.fields?.expirationDate?.value;

  const isGlobalAlertExpired =
    globalAlertExpirationDate && globalAlertExpirationDate !== isDefaultDate
      ? new Date(globalAlertExpirationDate) < new Date()
      : false;

  // Use global alert if it exists and isn't expired, otherwise use page alert
  const dataField = globalPageAlert && !isGlobalAlertExpired ? globalPageAlert : fieldData;

  //check if the selected alert is expired
  const expirationDate = dataField?.fields?.expirationDate?.value;
  const isExpired =
    expirationDate && expirationDate !== isDefaultDate
      ? new Date(expirationDate) < new Date()
      : false;

  const IsWarning = fieldData?.fields?.isWarning?.value;
  const [isOpen, setIsOpen] = useState(true);
  const [hasCheckedCookie, setHasCheckedCookie] = useState(false); // To add loading check before rednering the alert.

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const closedAlertIds = getCookie('closedAlertIds');
      const parsedIds = closedAlertIds ? JSON.parse(closedAlertIds) : [];

      if (parsedIds.includes(dataField?.id)) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
      setHasCheckedCookie(true);
    }
  }, [dataField?.id]);

  const onEnterKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(false);
    }
  };

  const onCloseAlert = (id: string) => {
    if (typeof window !== 'undefined') {
      const closedAlertIds = getCookie('closedAlertIds');
      const parsedIds = closedAlertIds ? JSON.parse(closedAlertIds) : [];

      if (!parsedIds.includes(id)) {
        parsedIds.push(id);
        setCookie('closedAlertIds', JSON.stringify(parsedIds));
      }
      setIsOpen(false);
    }
  };

  const getAlertTheme = () => {
    // Check if isWarning flag exists
    if (dataField?.fields?.isWarning?.value !== undefined) {
      return dataField.fields.isWarning.value ? 'theme-red' : 'theme-aqua';
    }

    // Fallback to displayName
    return dataField?.displayName === 'Neutral' ? 'theme-aqua' : 'theme-red';
  };

  //Check alert theme/type
  const alertTheme = getAlertTheme();
  const isWarningAlert = alertTheme === 'theme-red';

  // Only render if the alert type matches the requested type
  const requestedType = props.type || 'neutral'; // Default to Neutral if not specified

  if (
    (requestedType === 'neutral' && isWarningAlert) ||
    (requestedType === 'warning' && !isWarningAlert)
  ) {
    return <></>;
  }

  if (
    isExpired ||
    !hasCheckedCookie ||
    ((dataField?.fields?.title?.value === undefined || dataField?.fields?.title?.value === '') &&
      (dataField?.fields?.description?.value === undefined ||
        dataField?.fields?.description?.value === ''))
  ) {
    return <></>;
  }

  return (
    <>
      {/* Wrapper  */}
      <div
        className={`${wrapper()} ${getAlertTheme()} ${!isOpen ? 'max-h-0 transform -translate-y-full opacity-0 pointer-events-none' : 'max-h-screen transform translate-y-0 opacity-100 pointer-events-auto'}`}
        role="alert"
        hidden={!isOpen}
      >
        {/* Content */}
        <div className={content()}>
          <div>
            <div
              className={titleWrapper()}
              role={`${IsWarning ? 'alert' : 'status'}`}
              aria-live={`${IsWarning ? 'assertive' : 'polite'}`}
            >
              {getAlertTheme() === 'theme-red' ? (
                <SvgIcon icon="info" className={alertIcon()} size="lg" aria-label="warning alert" />
              ) : (
                <SvgIcon icon="bell" className={alertIcon()} size="lg" aria-label="Neutral alert" />
              )}

              <Text field={dataField?.fields?.title} tag="p" className={title()} />

              {/* Close button for mobile and tablet view */}
              <button
                onClick={() => {
                  onCloseAlert(dataField?.id || '');
                }}
                className={`${closeButton({ desktopCloseButton: false })} ml-auto`}
                aria-label="Close"
              >
                <SvgIcon
                  icon="close"
                  size="lg"
                  className={closeIcon()}
                  ariaLabel="true"
                  aria-hidden={isOpen}
                />
              </button>
            </div>
            <div className={description()}>
              <RichTextWrapper field={dataField?.fields?.description} />
            </div>
          </div>
          <div className={linkWrapper()}>
            <LinkWrapper
              field={dataField?.fields?.buttonLink}
              ctaVariant="secondary"
              suppressNewTabIcon={false}
              className="w-full md:w-fit"
            />
            {/* Close button for Desktop view */}
            <button
              onClick={() => {
                onCloseAlert(dataField?.id || '');
              }}
              onKeyDown={(e) => onEnterKeyDown(e)}
              className={closeButton({ desktopCloseButton: true })}
              aria-label="Close"
            >
              <SvgIcon
                icon="close"
                size="lg"
                className={closeIcon()}
                ariaLabel="true"
                aria-hidden={isOpen}
              />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Alert;

export const TailwindVariants = tv({
  slots: {
    wrapper: 'mx-auto w-full bg-theme-bg transition-all duration-300 ease-in-out ',
    content:
      'lg:flex mx-auto max-w-screen-xxl justify-between pt-8 pb-12 items-center px-10 xl:px-20 md:pl-[40px]',
    titleWrapper: 'flex items-center py-5',
    alertIcon: '!text-theme-text leading-none mb-3',
    title:
      'lg:text-22 lg:leading-31 text-20 leading-28 pl-8 font-centuryGothic text-theme-text font-bold',
    closeIcon: 'text-theme-text-btn-disabled-text',
    linkWrapper: 'lg:flex items-center gap-[32px] pt-8 lg:pt-0 md:w-fit lg:pl-[32px]',
    description: 'pt-2 text-16 font-[500] leading-25 text-gray-900 md:pr-[80px] pr-0 lg:pr-0',
    closeButton: 'w-[48px] h-[48px] items-center justify-center',
  },
  variants: {
    desktopCloseButton: {
      true: {
        closeButton: 'lg:inline-flex hidden',
      },
      false: {
        closeButton: 'lg:hidden block',
      },
    },
  },
});
